import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, db } from "../../firebase/firebase";
import { useEffect, useRef, useState } from "react";
import {
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
  orderBy,
} from "firebase/firestore";
import { useAuth } from "../../context/AuthContext";
import prufyyLogo from "../../assets/prufyy-logo-white.png";

const ONBOARDING_KEY = "prufyy_evaluator_onboarding_seen";

function EvaluatorDashboardLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [unreadCount, setUnreadCount] = useState(0);
  const [progressUnread, setProgressUnread] = useState(0);
  const prevUnreadRef = useRef(0);
  const [cohortInviteCount, setCohortInviteCount] = useState(0);

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);

  const handleLogout = () => {
    navigate("/logout");
  };

  /* =========================
     FIRST-TIME ONBOARDING TRIGGER
     ========================= */
  useEffect(() => {
    if (user?.uid) {
      const seen = localStorage.getItem(ONBOARDING_KEY);
      if (!seen) {
        setShowOnboarding(true);
      }
    }
  }, [user?.uid]);

  /* =========================
     🎨 VISUAL STYLES
     ========================= */
  const sectionTitleStyle = {
    marginTop: 28,
    marginBottom: 10,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.12em",
    color: "#94a3b8",
    textTransform: "uppercase",
  };

  const navItemStyle = ({ isActive }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 14px",
    borderRadius: 10,
    fontSize: 14,
    fontWeight: isActive ? 600 : 500,
    color: isActive ? "#0f172a" : "#e2e8f0",
    background: isActive
      ? "linear-gradient(135deg,#ffffff,#e2e8f0)"
      : "transparent",
    textDecoration: "none",
    marginBottom: 6,
    transition: "all 0.25s ease",
  });

  const badgeStyle = (color) => ({
    background: color,
    color: "#fff",
    borderRadius: 999,
    fontSize: 11,
    padding: "2px 8px",
    fontWeight: 600,
    boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
  });

  /* =========================
     SHARED WITH ME (UNREAD)
     ========================= */
  useEffect(() => {
    if (!user?.uid) return;
    const loadUnreadCount = async () => {
      try {
        const sharesSnap = await getDocs(
          query(collection(db, "shares"), where("evaluatorId", "==", user.uid))
        );

        const sharedFounderIds = new Set();
        sharesSnap.docs.forEach((d) => {
          const { founderId } = d.data();
          if (founderId) sharedFounderIds.add(founderId);
        });

        const evalSnap = await getDocs(
          query(collection(db, "evaluations"), where("evaluatorId", "==", user.uid))
        );

        const evaluatedFounderIds = new Set();
        evalSnap.docs.forEach((d) => {
          const { founderId } = d.data();
          if (founderId) evaluatedFounderIds.add(founderId);
        });

        let unread = 0;
        sharedFounderIds.forEach((id) => {
          if (!evaluatedFounderIds.has(id)) unread++;
        });

        prevUnreadRef.current = unread;
        setUnreadCount(unread);
      } catch (err) {
        console.error("Failed to load unread count", err);
      }
    };
    loadUnreadCount();
  }, [user?.uid, location.pathname]);

  /* =========================
     🔔 PROGRESS FEED NOTIFICATIONS
     ========================= */
  useEffect(() => {
    if (!user?.uid) return;
    let unsubscribe = () => {};

    const setupProgressListener = async () => {
      try {
        const sharesSnap = await getDocs(
          query(collection(db, "shares"), where("evaluatorId", "==", user.uid))
        );

        const founderIds = sharesSnap.docs
          .map((d) => d.data().founderId)
          .filter(Boolean);

        if (founderIds.length === 0) return;

        const lastSeenKey = `progress_last_seen_${user.uid}`;
        const lastSeen = localStorage.getItem(lastSeenKey);

        const q = query(
          collection(db, "progressPosts"),
          where("founderId", "in", founderIds),
          orderBy("createdAt", "desc")
        );

        unsubscribe = onSnapshot(q, (snap) => {
          let count = 0;
          snap.docs.forEach((d) => {
            const data = d.data();
            if (!data.createdAt) return;
            const created = data.createdAt.toMillis();
            if (!lastSeen || created > Number(lastSeen)) count++;
          });

          if (location.pathname === "/evaluator/progress-feed") {
            localStorage.setItem(lastSeenKey, Date.now().toString());
            setProgressUnread(0);
          } else {
            setProgressUnread(count);
          }
        });
      } catch (err) {
        console.error("Progress feed notification error", err);
      }
    };

    setupProgressListener();
    return () => unsubscribe();
  }, [user?.uid, location.pathname]);

  /* =========================
     COHORT INVITES
     ========================= */
  useEffect(() => {
    if (!user?.uid) return;

    const q = query(
      collection(db, "cohortInvites"),
      where("invitedUserId", "==", user.uid),
      where("status", "==", "pending")
    );

    const unsub = onSnapshot(q, (snap) => {
      setCohortInviteCount(snap.size);
    });

    return () => unsub();
  }, [user?.uid]);

  /* =========================
     ONBOARDING CONTENT
     ========================= */
  const onboardingSteps = [
    {
      title: "Welcome, Evaluator",
      text:
        "You’ve been invited to review startups on Prufyy. Your role is advisory — not certification or verification.",
    },
    {
      title: "Shared with you",
      text:
        "Startups appear here when founders share access. You choose what and when to review — nothing is mandatory.",
    },
    {
      title: "Draft vs Submitted",
      text:
        "Opening a startup creates a private draft. Founders only see your feedback after you submit it.",
    },
    {
      title: "Progress & Cohorts",
      text:
        "Progress Feed shows founder updates. Cohorts are optional groups for structured evaluations.",
    },
    {
      title: "Responsible evaluation",
      text:
        "Assess clarity, evidence, and reasoning. You’re contributing perspective — not declaring truth.",
    },
  ];

  const closeOnboarding = () => {
    localStorage.setItem(ONBOARDING_KEY, "true");
    setShowOnboarding(false);
    setOnboardingStep(0);
  };

  return (
    <>
      {/* ================= ONBOARDING OVERLAY ================= */}
      {showOnboarding && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,23,42,0.65)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <div
            style={{
              maxWidth: 520,
              width: "100%",
              background: "#fff",
              borderRadius: 16,
              padding: 28,
              boxShadow: "0 30px 80px rgba(0,0,0,0.3)",
            }}
          >
            <h2 style={{ marginBottom: 12 }}>
              {onboardingSteps[onboardingStep].title}
            </h2>
            <p style={{ color: "#475569", lineHeight: 1.6 }}>
              {onboardingSteps[onboardingStep].text}
            </p>

            <div
              style={{
                marginTop: 24,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <button
                className="btn-secondary"
                onClick={closeOnboarding}
              >
                Skip
              </button>

              <div style={{ display: "flex", gap: 8 }}>
                {onboardingStep < onboardingSteps.length - 1 ? (
                  <button
                    className="btn-primary"
                    onClick={() =>
                      setOnboardingStep((s) => s + 1)
                    }
                  >
                    Next
                  </button>
                ) : (
                  <button
                    className="btn-primary"
                    onClick={closeOnboarding}
                  >
                    Finish
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 🌟 HOVER EFFECT STYLES */}
      <style>{`
        .eval-nav-item:hover {
          background: linear-gradient(135deg, rgba(255,255,255,0.15), rgba(255,255,255,0.08));
          transform: translateX(4px);
          box-shadow: 0 4px 14px rgba(0,0,0,0.15);
        }
        .eval-nav-item span {
          transition: transform 0.2s ease;
        }
        .eval-nav-item:hover span:first-child {
          transform: translateX(2px);
        }
      `}</style>

      <div className="dashboard" style={{ display: "flex", minHeight: "100vh" }}>
        <aside
          style={{
            width: 260,
            background: "linear-gradient(180deg, #0f172a 0%, #1e293b 100%)",
            color: "#fff",
            display: "flex",
            flexDirection: "column",
            boxShadow: "4px 0 20px rgba(0,0,0,0.15)",
          }}
        >
          <div style={{ padding: "24px 16px", textAlign: "center" }}>
            <img src={prufyyLogo} alt="prufyy" style={{ width: 150 }} />
          </div>

          <nav style={{ padding: "18px 14px", flex: 1 }}>
            <div style={sectionTitleStyle}>Account</div>
            <NavLink to="/evaluator/profile" style={navItemStyle} className="eval-nav-item">
              <span>Profile</span>
            </NavLink>

            <div style={sectionTitleStyle}>Evaluation</div>
            <NavLink to="/evaluator/shared" style={navItemStyle} className="eval-nav-item">
              <span>Shared with me</span>
              {unreadCount > 0 && (
                <span style={badgeStyle("#ef4444")} title="New startups not yet reviewed">
                  {unreadCount}
                </span>
              )}
            </NavLink>

            <NavLink to="/evaluator/reviews" style={navItemStyle} className="eval-nav-item">
              <span>My Reviews</span>
            </NavLink>

            <div style={sectionTitleStyle}>Organization</div>
            <NavLink to="/evaluator/cohorts" style={navItemStyle} className="eval-nav-item">
              <span>Cohorts</span>
              {cohortInviteCount > 0 && (
                <span style={badgeStyle("#3b82f6")} title="Pending cohort invitations">
                  {cohortInviteCount}
                </span>
              )}
            </NavLink>

            <div style={sectionTitleStyle}>Insights</div>
            <NavLink to="/evaluator/progress-feed" style={navItemStyle} className="eval-nav-item">
              <span>Progress Feed</span>
              {progressUnread > 0 && (
                <span style={badgeStyle("#16a34a")} title="New founder updates">
                  {progressUnread}
                </span>
              )}
            </NavLink>

            <NavLink to="/evaluator/analytics" style={navItemStyle} className="eval-nav-item">
              <span>Analytics</span>
            </NavLink>
          </nav>
        </aside>

        <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <header
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "14px 24px",
              background: "#fff",
            }}
          >
            <button
              className="btn-secondary"
              onClick={() => {
                setShowOnboarding(true);
                setOnboardingStep(0);
              }}
            >
              Help
            </button>

            <button className="btn-secondary" onClick={handleLogout}>
              Logout
            </button>
          </header>

          <main
            style={{
              padding: 28,
              background: "linear-gradient(180deg,#f8fafc,#eef2f7)",
              flex: 1,
            }}
          >
            <Outlet />
          </main>
        </div>
      </div>
    </>
  );
}

export default EvaluatorDashboardLayout;
